// src/loaders/objLoader.js
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader.js'
import { MTLLoader } from 'three/examples/jsm/loaders/MTLLoader.js'
import { BufferGeometryUtils } from 'three/examples/jsm/Addons.js'
import * as THREE from 'three'
import {
  createStandardMaterial,
  createMesh,
  weldOnPositionPreservingColor,
  centerAndOrientGeometry,
  setupColorAttribute,
  setupWeightAttribute,
  bakeTextureToVertexColors,
} from './loaderUtils'

/**
 * Load OBJ model from a path, Blob URL, or file data object
 * @param {string|Object} pathOrData - File path, Blob URL, or object with { objUrl, mtlUrl, textureUrls }
 * @param {Object} [options]
 * @param {boolean} [options.skipOrientation=false] - Skip the standard rotation. Set for
 *   checkpoint reloads, where orientation is already baked into the stored geometry.
 * @param {boolean} [options.preprocessed=false] - Skip the vertex weld for finalized
 *   CSG/display meshes that would otherwise take a very long time to merge.
 * @returns {Promise<THREE.Mesh>}
 */
export function loadOBJ(pathOrData, options = {}) {
  const { skipOrientation = false, preprocessed = false } = options
  return new Promise((resolve, reject) => {
    let isFileData = false;
    let objPath = pathOrData;
    let mtlPath = null;
    let textureMap = null;
    let textureManager = null;

    // Check if this is file data from uploaded files
    if (typeof pathOrData === 'object') {
      isFileData = true;
      objPath = pathOrData.objUrl;
      mtlPath = pathOrData.mtlUrl;

      // Create a map of texture names to Blob URLs for easy lookup
      textureMap = {};
      if (pathOrData.textureUrls) {
        const normalizeTextureKey = (raw) => {
          if (raw == null) return '';
          let s = String(raw);
          // Drop query/hash if present (defensive; some loaders may append these)
          const q = s.indexOf('?');
          const h = s.indexOf('#');
          const cut = q >= 0 && h >= 0 ? Math.min(q, h) : q >= 0 ? q : h;
          if (cut >= 0) s = s.slice(0, cut);
          // Support both URL paths and Windows paths.
          s = s.replace(/\\/g, '/');
          // Drop leading ./ segments
          while (s.startsWith('./')) s = s.slice(2);
          // Take basename
          const base = s.split('/').pop() || '';
          return base;
        };
        const addAliases = (name, url) => {
          if (!name || !url) return;
          const base = normalizeTextureKey(name);
          const keys = new Set([name, base, normalizeTextureKey(name)]);
          if (base) {
            keys.add(`source__${base}`);
            keys.add(`source_${base}`);
            // Also allow matching without those prefixes.
            if (base.startsWith('source__')) keys.add(base.replace(/^source__/, ''));
            if (base.startsWith('source_')) keys.add(base.replace(/^source_/, ''));
          }
          keys.forEach((k) => {
            if (k) textureMap[k] = url;
          });
        };
        pathOrData.textureUrls.forEach((tex) => addAliases(tex.name, tex.url));
      }

      // If we have textures, create a custom LoadingManager upfront
      if (Object.keys(textureMap).length > 0) {
        textureManager = new THREE.LoadingManager();

        // Intercept texture loading and map texture names to Blob URLs
        textureManager.setURLModifier((url) => {
          const filename = (() => {
            if (url == null) return '';
            let s = String(url);
            const q = s.indexOf('?');
            const h = s.indexOf('#');
            const cut = q >= 0 && h >= 0 ? Math.min(q, h) : q >= 0 ? q : h;
            if (cut >= 0) s = s.slice(0, cut);
            s = s.replace(/\\/g, '/');
            while (s.startsWith('./')) s = s.slice(2);
            return s.split('/').pop() || s;
          })();

          // Check if we have a Blob URL for this texture
          if (textureMap[filename]) {
            console.log(`Texture mapper: ${filename} -> Blob URL`);
            return textureMap[filename];
          }

          // Common prefix mismatch: server-stored names may be prefixed (e.g. source__*)
          if (filename.startsWith('source__') && textureMap[filename.replace(/^source__/, '')]) {
            const key = filename.replace(/^source__/, '');
            console.log(`Texture mapper: ${filename} -> ${key} (alias)`);
            return textureMap[key];
          }
          if (filename.startsWith('source_') && textureMap[filename.replace(/^source_/, '')]) {
            const key = filename.replace(/^source_/, '');
            console.log(`Texture mapper: ${filename} -> ${key} (alias)`);
            return textureMap[key];
          }
          if (textureMap[`source__${filename}`]) {
            const key = `source__${filename}`;
            console.log(`Texture mapper: ${filename} -> ${key} (alias)`);
            return textureMap[key];
          }
          if (textureMap[`source_${filename}`]) {
            const key = `source_${filename}`;
            console.log(`Texture mapper: ${filename} -> ${key} (alias)`);
            return textureMap[key];
          }

          // Also check by exact match
          if (textureMap[url]) {
            return textureMap[url];
          }

          console.log(`Texture mapper: ${filename} not found in map, using original URL`);
          return url;
        });
      }
    } else {
      // Original behavior for file paths
      const basePath = pathOrData.substring(0, pathOrData.lastIndexOf('/') + 1)
      mtlPath = pathOrData.replace('.obj', '.mtl')
    }

    // Try to load MTL first
    const loadMTL = new Promise((resolveMTL) => {
      if (!mtlPath) {
        resolveMTL(null);
        return;
      }

      const mtlLoader = textureManager
        ? new MTLLoader(textureManager)
        : new MTLLoader();

      if (!isFileData || !textureManager) {
        // Original behavior for file paths
        const basePath = objPath.substring(0, objPath.lastIndexOf('/') + 1)
        mtlLoader.setPath(basePath)
      }

      mtlLoader.load(
        mtlPath,
        (materials) => {
          materials.preload();
          resolveMTL(materials);
        },
        undefined,
        () => resolveMTL(null) // MTL not found, continue without it
      );
    })

    loadMTL.then((materials) => {
      const objLoader = new OBJLoader()

      if (materials) {
        objLoader.setMaterials(materials)
      }

      objLoader.load(
        objPath,
        (root) => {
          // Collect EVERY mesh. OBJ starts a new mesh at each `g`/`usemtl` boundary,
          // so a scan routinely arrives as several submeshes; keeping only the first
          // silently dropped the rest. STL/PLY are always single-mesh, so this never
          // showed up on those formats.
          const submeshes = []
          root.traverse((child) => {
            if (child.isMesh && child.geometry) submeshes.push(child)
          })
          if (submeshes.length === 0) return reject(new Error('No mesh in OBJ'))

          // Bake each submesh's texture to vertex colors BEFORE welding, then drop
          // `uv`. mergeVertices only welds vertices whose *every* attribute matches,
          // so carrying `uv` through the weld leaves the mesh split along each UV
          // seam — the adjacency cracks that break the index-walking tools
          // (smoothing, fill, trimline, carve). Nothing downstream wants the map:
          // the standard material renders with vertexColors, and the locking CSG
          // path synthesises its own zero-UV attribute in prepareGeometryForCSG.
          const parts = submeshes.map((child) => {
            const g = child.geometry.index ? child.geometry.toNonIndexed() : child.geometry
            const material = Array.isArray(child.material) ? child.material[0] : child.material
            const texture = material?.map

            let colorAttribute = null
            if (g.hasAttribute('uv') && texture?.image?.complete) {
              colorAttribute = bakeTextureToVertexColors(g, texture)
            }
            if (colorAttribute) {
              g.setAttribute('color', colorAttribute)
            } else if (!g.hasAttribute('color')) {
              // White, so every part carries the same attribute set for the merge below.
              const count = g.attributes.position.count
              const white = new Float32Array(count * 3).fill(1)
              g.setAttribute('color', new THREE.BufferAttribute(white, 3))
            }

            for (const name of Object.keys(g.attributes)) {
              if (name !== 'position' && name !== 'color') g.deleteAttribute(name)
            }
            g.clearGroups()
            return g
          })

          let geom
          if (parts.length === 1) {
            geom = parts[0]
          } else {
            geom = BufferGeometryUtils.mergeGeometries(parts, false)
            if (!geom) {
              return reject(
                new Error(`OBJ: could not combine ${parts.length} submeshes (mismatched attributes)`)
              )
            }
            console.log(`[loadOBJ] Combined ${parts.length} submeshes → ${geom.attributes.position.count} vertices`)
            parts.forEach((p) => p.dispose())
          }

          // Checkpoint reloads already have the standard rotation baked in; rotating
          // again would drift the model off-axis on every undo/step-revert.
          centerAndOrientGeometry(geom, { orient: !skipOrientation })

          if (preprocessed) {
            // Finalized manufacturing meshes are display-only CSG triangle soup;
            // welding them can hang for a long time. Same guard the STL/PLY path uses.
            console.log('[loadOBJ] Merge skipped (preprocessed mesh) — computing normals only')
            geom.computeVertexNormals()
          } else {
            // **CRITICAL**: the weld returns a NEW indexed geometry object; capture
            // the return value to ensure proper indexing across all loaders.
            geom = weldOnPositionPreservingColor(geom, 1e-4, 'OBJ')
          }

          setupColorAttribute(geom)
          setupWeightAttribute(geom)

          // Create mesh with standard material
          const finalMesh = createMesh(geom, 'OBJMesh')
          // Match the STL/PLY phased loader's shape so consumers get an object rather
          // than undefined. OBJ runs its topology validation inside
          // mergeAndNormalizeGeometry, which does not hand the result back.
          finalMesh.userData.meshStats = { simplify: null, topology: null }
          resolve(finalMesh)
        },
        undefined,
        reject
      )
    })
  })
}
